/**
 * RolePermissionsPanel — Admin > System > Roles.
 *
 * One table of what each role may do. Ticking a box gives that permission
 * to every user with the role; the server checks the same table on every
 * request, so a change applies to the API at once and to a user's screens
 * the next time their app loads. Admin always has everything and is not
 * shown. Saving and resetting need the action passcode.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { api } from '../../lib/store';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Save, ShieldCheck } from 'lucide-react';

const sameSet = (a = [], b = []) => a.length === b.length && a.every((k) => b.includes(k));

export default function RolePermissionsPanel() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.getRolePermissions()
    .then((r) => { setData(r.data); setDraft(r.data.matrix); })
    .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load roles'));

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const out = [];
    (data?.permissions || []).forEach((p) => {
      let g = out.find((x) => x.name === p.group);
      if (!g) { g = { name: p.group, items: [] }; out.push(g); }
      g.items.push(p);
    });
    return out;
  }, [data]);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12 text-heda-sub">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading roles…
      </div>
    );
  }

  const roles = data.roles;
  const dirty = roles.some((r) => !sameSet(draft[r.key], data.matrix[r.key]));

  const toggle = (role, key) => {
    setDraft((d) => {
      const cur = d[role] || [];
      return { ...d, [role]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });
  };

  const run = async (call, done) => {
    const passcode = window.prompt('Enter the action passcode to confirm:');
    if (!passcode) return;
    setSaving(true);
    try {
      const r = await call(passcode);
      setData(r.data);
      setDraft(r.data.matrix);
      toast.success(done);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="roles-panel">
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <ShieldCheck className="w-4 h-4 text-heda-gold" /> Roles &amp; Permissions
            </CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => run(api.resetRolePermissions, 'Permissions reset to the defaults')}
                className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-9 text-[12.5px]"
                data-testid="roles-reset-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Defaults
              </Button>
              <Button
                size="sm"
                disabled={!dirty || saving}
                onClick={() => run((pc) => api.saveRolePermissions(draft, pc), 'Permissions saved')}
                className="rounded-xl bg-heda-olive text-white shadow-none hover:bg-heda-olive-dark h-9 px-4 text-[13px] font-semibold"
                data-testid="roles-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-heda-mute mt-1">
            Admin always has every permission. A change applies to the server at once and to a person&rsquo;s
            screens the next time their app opens. A dot marks a box that differs from the default.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="roles-table">
              <thead className="heda-thead">
                <tr>
                  <th className="sticky left-0 z-10 bg-heda-cream px-3 sm:px-4 py-2.5 font-semibold text-left min-w-[150px] sm:min-w-[220px]">Permission</th>
                  {roles.map((r) => (
                    <th key={r.key} className="px-3 sm:px-4 py-2.5 font-semibold text-center whitespace-nowrap">{r.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.name}>
                    <tr>
                      <td colSpan={roles.length + 1} className="border-t border-heda-line bg-heda-cream/40 px-3 sm:px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-heda-gold">
                        {g.name}
                      </td>
                    </tr>
                    {g.items.map((p) => (
                      <tr key={p.key} className="border-t border-heda-line">
                        <td className="sticky left-0 z-10 bg-white px-3 sm:px-4 py-2.5 text-heda-ink">{p.label}</td>
                        {roles.map((r) => {
                          const on = (draft[r.key] || []).includes(p.key);
                          const byDefault = (data.defaults[r.key] || []).includes(p.key);
                          return (
                            <td key={r.key} className="px-3 sm:px-4 py-2.5 text-center">
                              <span className="inline-flex items-center gap-1">
                                <Checkbox
                                  className="min-h-0"
                                  checked={on}
                                  onCheckedChange={() => toggle(r.key, p.key)}
                                  aria-label={`${r.label}: ${p.label}`}
                                  data-testid={`perm-${r.key}-${p.key}`}
                                />
                                <span className={`w-1.5 h-1.5 rounded-full ${on !== byDefault ? 'bg-heda-gold' : 'bg-transparent'}`} />
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-heda-line bg-white shadow-none">
          <CardHeader className="border-b border-heda-line px-5 py-3">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">Every signed-in user</CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-4 text-xs text-heda-ink space-y-1">
            {data.everyone.map((t) => <p key={t}>{t}</p>)}
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-heda-line bg-white shadow-none">
          <CardHeader className="border-b border-heda-line px-5 py-3">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">Admin only</CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-4 text-xs text-heda-ink space-y-1">
            {data.admin_only.map((t) => <p key={t}>{t}</p>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
